/**
 * Transforms raw plan data from DynamoDB into a format suitable for the UI.
 * Matches the "Marketing Intent" API structure (plan.ui).
 */
export const transformPlans = (plans = []) => {
  if (!plans || !Array.isArray(plans)) return [];

  const transformed = plans.map((plan) => {
    if (!plan) return null;
    // 1. Map from the new API structure
    const ui = plan.ui || {};
    const price = plan.price || {};
    const exponent = price.exponent ?? 0;
    const divisor = Math.pow(10, exponent);

    // Determine billing cycle correctly
    const billingCycle = plan.billing_cycle || plan.billingCycle;

    // 2. Base calculations
    const totalPrice = price.amount / divisor;
    let duration, period;

    switch (billingCycle) {
      case 'MONTHLY':
        duration = '1 Month';
        period = 'monthly';
        break;
      case 'QUARTERLY':
        duration = '3 Months';
        period = 'quarterly';
        break;
      case 'HALF_YEARLY':
        duration = '6 Months';
        period = 'half-yearly';
        break;
      case 'YEARLY':
        duration = '1 Year';
        period = 'yearly';
        break;
      default:
        duration = billingCycle;
        period = (billingCycle || '').toLowerCase();
    }

    const monthlyPrice = Math.round(totalPrice / (billingCycle === 'MONTHLY' ? 1 : billingCycle === 'QUARTERLY' ? 3 : billingCycle === 'HALF_YEARLY' ? 6 : 12));
    const gstRate = (price.gst_rate || price.gstRate || 0) / 100;

    const displayFeatures = ui.displayFeatures || [];
    const isPopular = ui.isPopular === true || ui.isPopular === 'true';
    const badgeText = ui.badgeText || null;
    const tagline = ui.tagline || null;
    const featuresHeader = ui.featuresHeader || "What you will get:";
    const discountPercent = price.discountPercentage || 0;

    // 3. Savings and Daily Cost Calculations (Pre-calculated by Backend)
    const savingsAmount = price.savingsAmount || 0;
    const dailyCost = price.dailyCost || 0;
    const priceTitle = price.displayPriceTitle || `₹${totalPrice.toLocaleString()}`;
    const priceSubtitle = price.displayPriceSubtitle || `/ ${period}`;

    return {
      ...plan,
      id: plan.planId || plan.plan_id,
      name: plan.name,
      billingCycle,
      badgeText,
      displayFeatures,
      featuresHeader,
      isPopular,
      tagline,
      price: {
        ...price,
        amount: price.amount,
        originalAmount: price.originalAmount || price.original_amount,
        displayPriceTitle: priceTitle,
        displayPriceSubtitle: priceSubtitle,
        trialLabel: price.trialLabel || price.trial_label,
        trialPrice: price.trialPrice || price.trial_price,
        savingsAmount,
        dailyCost,
        dailyCostText: dailyCost > 0 ? `₹${dailyCost}/day` : "",
      },
      totalPrice,
      monthlyPrice,
      gstRate,
      gstAmount: (price.gstAmount ?? 0) / divisor,
      totalWithGst: (price.amountWithGst ?? 0) / divisor,
      duration,
      period,
      discount: discountPercent ? `Save ${discountPercent}%` : '',
      popular: isPopular,
    };
  });

  // Enforce strict ordering: Starter always first, Growth second.
  const planOrder = { 'Starter': 1, 'Growth': 2 };
  
  return transformed.filter(Boolean).sort((a, b) => {
    const orderA = planOrder[a.name] || 99;
    const orderB = planOrder[b.name] || 99;
    // If they have the same order priority (or both are unknown), fall back to price sorting
    if (orderA === orderB) {
      return a.totalPrice - b.totalPrice;
    }
    return orderA - orderB;
  });
};
